import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Confetti from "react-confetti";
import {
  Button,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import {
  submitPicksAction,
  updateTiebreakerAnswerAction,
} from "../../store/reducers/picks";
import { closeModal } from "../../store/reducers/modalConductor";
import {
  ModalActions,
  ModalHeader,
  ModalWrapper,
} from "../RootModal/ModalComponents";

// tiebreaker_answer is a smallint in the DB (max 32767) and the value is a
// "total goals" guess — realistically a few hundred. Bound the input so a
// fat-fingered number can't overflow the column, which the API surfaces only
// as a generic 422 "internal error" rather than a useful message.
const MAX_TIEBREAKER = 999;

const validateTiebreaker = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  if (!Number.isInteger(n)) return "Please enter a whole number";
  if (n < 1 || n > MAX_TIEBREAKER) {
    return `Enter a number between 1 and ${MAX_TIEBREAKER}`;
  }
  return undefined;
};

const SubmitPicksModal = (props) => {
  const { userId, contestId } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contestType = useSelector((state) => state.app?.contestType);
  const isKnockout = contestType === "Knockout";

  const [tiebreakerAnswer, setTiebreakerAnswer] = useState(null);
  const [fieldError, setFieldError] = useState();
  const [startConfetti, setStartConfetti] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  // react-confetti needs explicit pixel dimensions; track the viewport so the
  // burst covers the whole screen (and stays correct on resize / rotate).
  const [winSize, setWinSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));
  useEffect(() => {
    const onResize = () =>
      setWinSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
    if (isFinished) {
      navigate("/games");
    }
  }, [dispatch, isFinished, navigate]);

  const handleSubmitPicks = async () => {
    const validationError = validateTiebreaker(tiebreakerAnswer);
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    const answer = Number(tiebreakerAnswer);
    try {
      // dispatch() on a createAsyncThunk resolves to the action object, not
      // the payload. .unwrap() returns the payload and throws on rejection, so
      // a clean resolve == saved; if the tiebreaker save fails we never try to
      // submit.
      await dispatch(
        updateTiebreakerAnswerAction({ userId, contestId, answer }),
      ).unwrap();

      await dispatch(submitPicksAction({ userId, contestId })).unwrap();

      setStartConfetti(true);
      setIsFinished(true);
    } catch (err) {
      // Thunks reject with the server's response body ({ version, error }), so
      // show that message when present (e.g. "Picks have already been
      // submitted") instead of a generic one that hides the real reason.
      console.error("Error submitting picks: ", err);
      setFieldError(
        err?.error || "Something went wrong submitting your picks. Try again.",
      );
    }
  };

  useEffect(() => {
    if (startConfetti) {
      setTimeout(() => {
        setStartConfetti(false);
      }, 5000);
    }
  }, [startConfetti]);

  return (
    // RootModal only mounts this component once SUBMIT_PICKS_MODAL is the
    // active modalType, so "mounted" already means "should be open". Pass show
    // explicitly because ModalWrapper gates the MUI Dialog on open={props.show};
    // without it the dialog renders closed and nothing appears on screen.
    <ModalWrapper show size="xs" disableFullScreen disableScrollLock>
      {/* Full-screen confetti, portaled to <body> so it isn't clipped to the
          dialog box. The timed stop is driven by `recycle` (not `run`) so the
          pieces keep falling and exit naturally instead of freezing mid-air. */}
      {isFinished &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              pointerEvents: "none",
              zIndex: theme.zIndex.modal + 10,
            }}
          >
            <Confetti
              width={winSize.width}
              height={winSize.height}
              numberOfPieces={500}
              gravity={0.22}
              initialVelocityY={14}
              recycle={startConfetti}
              tweenDuration={6000}
              colors={[
                //gold, silver, and bronze confetti
                theme.branding.gray[300],
                theme.branding.yellow[700],
                theme.branding.gold[300],
                theme.branding.gold[500],
              ]}
            />
          </div>,
          document.body,
        )}
      <ModalHeader handleClose={handleClose} />
      <Grid container direction="column" sx={{ gap: "16px" }}>
        <Grid
          container
          direction="column"
          sx={{ gap: "12px", padding: "0 24px 4px" }}
        >
          <Grid
            container
            direction="column"
            sx={{
              gap: "8px",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Grid
              container
              sx={{
                height: "40px",
                width: "40px",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "100%",
                background: `linear-gradient(135deg, ${theme.branding.gold[500]} 20%, ${theme.branding.yellow[700]} 100%)`,
              }}
            >
              <EmojiEventsOutlinedIcon
                sx={{
                  color: theme.branding.gray[0],
                  height: "24px",
                  width: "24px",
                }}
              />
            </Grid>
            <Grid
              container
              direction="column"
              sx={{
                gap: "4px",
                textAlign: "center",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4">Complete Your Bracket</Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.branding.gray[500] }}
              >
                Answer the Tiebreaker Question
              </Typography>
            </Grid>
          </Grid>
          <Grid container direction="column" sx={{ gap: "4px", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ color: theme.branding.gray[500] }}
            >
              Guess the total number of goals that will be scored over the
              course of the 2026 World Cup{isKnockout ? " Knockout" : ""}
            </Typography>
            <Grid container direction="column">
              <TextField
                variant="outlined"
                color={!!fieldError ? "error" : "primary"}
                type="number"
                placeholder="e.g. 170"
                value={tiebreakerAnswer ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setTiebreakerAnswer(value);
                  setFieldError(validateTiebreaker(value));
                }}
                slotProps={{
                  htmlInput: {
                    min: 1,
                    max: MAX_TIEBREAKER,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                }}
              />
              {!!fieldError && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.branding.red[500] }}
                >
                  {fieldError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <ModalActions>
          <Grid
            container
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Button
              variant="contained"
              onClick={() => {
                if (!isFinished) {
                  handleSubmitPicks();
                } else {
                  handleClose();
                }
              }}
              disabled={fieldError || !tiebreakerAnswer}
              fullWidth={isMobile}
              endIcon={
                !isFinished && (
                  <RocketLaunchOutlinedIcon
                    sx={{ color: theme.branding.gray[0] }}
                  />
                )
              }
              sx={{
                borderRadius: "10px",
                padding: "10px 24px",
                background:
                  tiebreakerAnswer > 0
                    ? `linear-gradient(90deg, ${theme.branding.cyan[300]} 0%, ${theme.branding.cyan[700]} 100%)`
                    : "auto",
                color: theme.branding.gray[0],
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 10px 20px -10px #33DDEE33`, //cyan[300] 20% opacity
                },
              }}
            >
              {isFinished ? "Check Games Schedule" : "Submit"}
            </Button>
          </Grid>
        </ModalActions>
      </Grid>
    </ModalWrapper>
  );
};

export default SubmitPicksModal;
